import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, useMediaQuery } from '@mui/material';
import { Page, FullTable as Table, MobileTable } from '../../components';
import { IEventAPI, getEventTypes } from '../../fetch/event';
import { useToastContext } from '../../hooks';
import { IEvent, IEventRes, IDropdownResponse } from '../../models';
import { Edit, DeleteOutlined } from '@mui/icons-material';
import { AddEditEventModal } from './AddEditEventModal';

export const Events: FC = () => {
  const { showToast } = useToastContext();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [events, setEvents] = useState<IEvent[]>([]);
  const [page, setPage] = useState(0);
  const [perPage, setRowsPerPage] = useState(10);
  const [recordCount, setRecordCount] = useState(0);
  const [selectedSort, setSelectedSort] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<{ [key: string]: 'Asc' | 'Desc' }>({});
  const [eventTypes, setEventTypes] = useState<IDropdownResponse[]>([]);
  const [loadingEventTypes, setLoadingEventTypes] = useState(false);
  const [editingEvent, setEditingEvent] = useState<IEvent | undefined>(undefined);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const fetchEventTypes = async () => {
    setLoadingEventTypes(true);
    try {
      const res = await getEventTypes();
      setEventTypes(res);
    } catch (error) {
      console.error(error);
      showToast('error', 'Error fetching event types.');
    } finally {
      setLoadingEventTypes(false);
    }
  };

  const fetchEvents = async () => {
    setLoadingEvents(true);
    try {
      const sortParams = selectedSort
        ? { sortBy: selectedSort, sortDirection: sortDirection[selectedSort] }
        : {};
      const eventsRes: IEventRes = await IEventAPI.getAll({
        page: page + 1,
        perPage,
        ...sortParams,
      });
      setEvents(eventsRes.records);
      setRecordCount(eventsRes.totalRecordCount);
    } catch (error: any) {
      showToast('error', error?.detail || 'Error retrieving events.');
    } finally {
      setLoadingEvents(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchEventTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, selectedSort, sortDirection]);

  const handleClickColumn = (column: string) => {
    // If no sort exists yet for this column, default to Asc.
    const currentDirection = sortDirection[column] || 'Asc';
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: currentDirection === 'Asc' ? 'Desc' : 'Asc',
    });
  };

  // Define table columns
  const columns = useMemo(() => {
    return [
      {
        accessor: 'isVisible',
        Header: 'Display Event',
        sort: false,
        Cell: ({
          cell: {
            row: { original },
          },
        }: any) => <span>{original.isVisible ? 'Yes' : 'No'}</span>,
      },
      {
        accessor: 'eventName',
        Header: 'Event Name',
        isServerSorted: selectedSort === 'eventName',
        isServerSortedDesc: sortDirection.eventName === 'Desc',
        handleClickColumn: () => handleClickColumn('eventName'),
      },
      {
        accessor: 'eventType',
        Header: 'Event Type',
        isServerSorted: selectedSort === 'eventType',
        isServerSortedDesc: sortDirection.eventType === 'Desc',
        handleClickColumn: () => handleClickColumn('eventType'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: any) => {
          const typeObj = eventTypes.find(et => et.value === original.eventType);
          return <span>{typeObj ? typeObj.description : original.eventType}</span>;
        },
      },
      {
        accessor: 'eventDateStart',
        Header: 'Event Date Start',
        isServerSorted: selectedSort === 'eventDateStart',
        isServerSortedDesc: sortDirection.eventDateStart === 'Desc',
        handleClickColumn: () => handleClickColumn('eventDateStart'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: any) => {
          const startDate = original.eventDateStart ? formatDate(original.eventDateStart) : '';
          return <span>{startDate}</span>;
        },
      },
      {
        accessor: 'eventDateEnd',
        Header: 'Event Date End',
        isServerSorted: selectedSort === 'eventDateEnd',
        isServerSortedDesc: sortDirection.eventDateEnd === 'Desc',
        handleClickColumn: () => handleClickColumn('eventDateEnd'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: any) => {
          const endDate = original.eventDateEnd ? formatDate(original.eventDateEnd) : '';
          return <span>{endDate}</span>;
        },
      },
      {
        accessor: 'eventLocation',
        Header: 'Event Location',
        sort: false,
        overrideWidth: '350px',
      },
      {
        accessor: 'actions',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original },
          },
        }: any) => (
          <>
            <IconButton onClick={() => setEditingEvent(original)}>
              <Edit />
            </IconButton>
            <IconButton onClick={() => handleDeleteEvent(original)} color="error">
              <DeleteOutlined />
            </IconButton>
          </>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventTypes, selectedSort, sortDirection]);

  const handleDeleteEvent = async (eventToDelete: IEvent) => {
    const result = window.confirm('Are you sure you want to remove this event?');
    if (!result) return;
    try {
      await IEventAPI.delete(eventToDelete.eventId);
      showToast('success', 'Event deleted successfully.');
      fetchEvents();
    } catch (error: any) {
      showToast('error', error?.detail || 'Error deleting event.');
    }
  };

  const handleSaveEvent = async (updatedEvent: IEvent) => {
    try {
      await IEventAPI.upsert(updatedEvent);
      showToast('success', updatedEvent.eventId ? 'Event updated.' : 'Event created.');
      fetchEvents();
    } catch (error: any) {
      showToast('error', error?.detail || 'Error saving event.');
    } finally {
      setEditingEvent(undefined);
    }
  };

  return (
    <Page title="Events">
      <Box>
        <Box display="flex" justifyContent="flex-end" mb={0.25}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setEditingEvent(IEvent.getDefault())}
          >
            Add Event
          </Button>
        </Box>
        <Table
          columns={columns}
          data={events}
          isLoading={loadingEvents || loadingEventTypes}
          stickyHeader
          serverPage={page}
          serverRecordCount={recordCount}
          serverPerPage={perPage}
          handlePage={setPage}
          handleRowsPerPage={setRowsPerPage}
          ResponsiveComponent={isMobile ? MobileTable : undefined}
          mobileProps={{
            fields: columns
              .filter(col => col.Header)
              .map(col => ({
                name: col.Header,
                accessor: col.accessor,
                Cell: col?.Cell,
              })),
            mobileAccordionLabel: (original: any) => original.eventName,
            handleEdit: (original: any) => setEditingEvent(original),
          }}
        />
      </Box>
      <AddEditEventModal
        open={!!editingEvent}
        event={editingEvent}
        eventTypes={eventTypes}
        onClose={() => setEditingEvent(undefined)}
        onSave={handleSaveEvent}
      />
    </Page>
  );
};
